import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Users, SignOut, ArrowsClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

/**
 * Admin-facing view of every currently signed-in session inside the caller's
 * scope. super_admin sees all tenants; tenant_admin sees just their tenant;
 * regular users never see this component (parent guards on role).
 *
 * Powered by GET /api/auth/admin/sessions + POST /api/auth/admin/sessions/kick.
 * Kicking is scope-gated on the backend so we don't need to filter here —
 * we simply refresh the list after a successful call.
 */
function summariseUA(ua) {
  if (!ua) return 'Unknown device';
  const browser =
    /Firefox\/(\d+)/.exec(ua) ? `Firefox ${RegExp.$1}` :
    /Edg\/(\d+)/.exec(ua) ? `Edge ${RegExp.$1}` :
    /Chrome\/(\d+)/.exec(ua) ? `Chrome ${RegExp.$1}` :
    /Safari\/(\d+)/.exec(ua) ? 'Safari' :
    'Browser';
  const os =
    /Windows NT/.test(ua) ? 'Windows' :
    /Mac OS X/.test(ua) ? 'macOS' :
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad|iOS/.test(ua) ? 'iOS' :
    /Linux/.test(ua) ? 'Linux' :
    'OS';
  return `${browser} · ${os}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function AdminSessionsPanel() {
  const { apiCall, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kicking, setKicking] = useState(null); // user_id currently being kicked
  const [confirmRow, setConfirmRow] = useState(null); // { user_id, user_name, user_email } | null
  const [error, setError] = useState('');
  const isSuper = user?.role === 'super_admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiCall('get', '/auth/admin/sessions');
      setRows(res?.data?.sessions ?? []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load active sessions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { refresh(); }, [refresh]);

  const requestKick = (row) => setConfirmRow(row);

  const performKick = async () => {
    if (!confirmRow) return;
    const row = confirmRow;
    setKicking(row.user_id);
    setConfirmRow(null);
    setError('');
    try {
      await apiCall('post', '/auth/admin/sessions/kick', { user_id: row.user_id });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to sign the user out');
    } finally {
      setKicking(null);
    }
  };

  return (
    <Card className="shadow-card rounded-lg border-0" data-testid="admin-sessions-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <Users size={14} className="inline mr-1.5" weight="bold" />Team sessions
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            disabled={loading}
            data-testid="admin-sessions-refresh"
            title="Refresh list"
            className="h-7 px-2"
          >
            <ArrowsClockwise size={13} weight="bold" className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {loading && (
          <div className="text-xs text-muted-foreground" data-testid="admin-sessions-loading">Loading…</div>
        )}

        {!loading && error && (
          <div className="text-xs text-rose-600 dark:text-rose-400" data-testid="admin-sessions-error">{error}</div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="text-xs text-muted-foreground" data-testid="admin-sessions-empty">
            No active sessions in {isSuper ? 'any tenant' : 'your tenant'} right now.
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="overflow-x-auto -mx-2" data-testid="admin-sessions-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-2 py-1.5">User</th>
                  {isSuper && <th className="text-left font-semibold px-2 py-1.5">Tenant</th>}
                  <th className="text-left font-semibold px-2 py-1.5">Device</th>
                  <th className="text-left font-semibold px-2 py-1.5">IP</th>
                  <th className="text-left font-semibold px-2 py-1.5">Signed in</th>
                  <th className="text-left font-semibold px-2 py-1.5">Last seen</th>
                  <th className="text-right font-semibold px-2 py-1.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.user_id}
                    className="border-t border-border align-top"
                    data-testid={`admin-session-row-${r.user_id}`}
                  >
                    <td className="px-2 py-2">
                      <div className="font-medium text-foreground truncate max-w-[200px]" title={r.user_name || r.user_email}>
                        {r.user_name || '—'}
                        {r.is_self && (
                          <span className="ml-1.5 px-1.5 py-px rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold uppercase tracking-wider">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-foreground/70 truncate max-w-[240px]">{r.user_email}</div>
                    </td>
                    {isSuper && (
                      <td className="px-2 py-2 text-foreground/80 truncate max-w-[160px]" title={r.tenant_name}>
                        {r.tenant_name || '—'}
                      </td>
                    )}
                    <td className="px-2 py-2 text-foreground/80 truncate max-w-[180px]" title={r.user_agent}>
                      {summariseUA(r.user_agent)}
                    </td>
                    <td className="px-2 py-2 text-foreground/80 whitespace-nowrap">{r.ip || 'Unknown'}</td>
                    <td className="px-2 py-2 text-foreground/80 whitespace-nowrap">{fmtTime(r.started_at)}</td>
                    <td className="px-2 py-2 text-foreground/80 whitespace-nowrap">{fmtTime(r.last_seen_at)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => requestKick(r)}
                        disabled={kicking === r.user_id || r.is_self}
                        title={r.is_self ? "You can't sign yourself out here — use the panel above." : "End this session"}
                        data-testid={`admin-session-kick-${r.user_id}`}
                        className="h-7 px-2 text-[11px]"
                      >
                        <SignOut size={12} weight="bold" className="mr-1" />
                        {kicking === r.user_id ? 'Signing out…' : 'Sign out'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <ConfirmDeleteDialog
          open={!!confirmRow}
          onOpenChange={(open) => { if (!open) setConfirmRow(null); }}
          title="Sign this user out?"
          description={
            confirmRow
              ? `${confirmRow.user_name || confirmRow.user_email} will be signed out of every device they're using. They'll need to log in again to continue.`
              : ''
          }
          onConfirm={performKick}
          destructiveLabel="Sign out"
        />
      </CardContent>
    </Card>
  );
}
